import { DARK_MODE_STORAGE_KEY, DARK_THEME, DATA_THEME_ATTR } from "@/constants/desgin-theme";
import { useEffect, useState } from "react";

export const THEME_CHANGE_EVENT = "metx-theme-change";

export type ColorScheme = "light" | "dark";

/**
 * Reads the currently applied theme directly from the DOM.
 */
export function isDarkModeEnabled(): boolean {
  if (typeof document === "undefined") {
    return false;
  }
  return document.documentElement.getAttribute(DATA_THEME_ATTR) === DARK_THEME;
}

/**
 * Applies the given theme to the document, persists it and notifies any
 * listeners (e.g. `useColorScheme`) so they can react to the change immediately.
 */
export function setDarkMode(dark: boolean): void {
  if (dark) {
    document.documentElement.setAttribute(DATA_THEME_ATTR, DARK_THEME);
  } else {
    document.documentElement.removeAttribute(DATA_THEME_ATTR);
  }
  localStorage.setItem(DARK_MODE_STORAGE_KEY, String(dark));
  window.dispatchEvent(new CustomEvent<boolean>(THEME_CHANGE_EVENT, { detail: dark }));
}

/**
 * Returns the currently active color scheme ("light" | "dark") and keeps
 * itself up to date whenever the theme is toggled, e.g. via the dark mode
 * switch, so that consumers (like Highcharts palette options) can react to it.
 */
export function useColorScheme(): ColorScheme {
  const [isDark, setIsDark] = useState<boolean>(() => isDarkModeEnabled());

  useEffect(() => {
    const handleThemeChange = (event: Event) => {
      const detail = (event as CustomEvent<boolean>).detail;
      setIsDark(typeof detail === "boolean" ? detail : isDarkModeEnabled());
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === DARK_MODE_STORAGE_KEY) {
        setIsDark(isDarkModeEnabled());
      }
    };

    window.addEventListener(THEME_CHANGE_EVENT, handleThemeChange);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, handleThemeChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return isDark ? "dark" : "light";
}
